import { tr } from '@/lib/i18n-core';
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Copy, Pencil, Plus, RotateCcw, Trash2 } from 'lucide-react';
import { del, get, post } from '@/lib/api';
import { describeError } from '@/lib/auth';
import { ActionMenu, ActionMenuItem, ActionMenuSeparator, Alert, Badge, Button } from '@/components/ui/primitives';
import { useToast } from '@/components/ui/toast';
import { SUPER_ADMIN, type Role, type RoleGroup } from './types';

function RoleActions({
  role,
  onEdit,
  onClone,
}: {
  role: Role;
  onEdit: (role: Role) => void;
  onClone: (role: Role) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();

  const remove = useMutation({
    mutationFn: () => del(`/org/roles/${role.id}`),
    onSuccess: () => {
      toast.success(tr('admin', 'x_deleted', { name: role.name }));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_delete_the_role'), describeError(caught)),
  });

  const reset = useMutation({
    mutationFn: () => post(`/org/roles/${role.id}/reset`),
    onSuccess: () => {
      toast.success(tr('admin', 'x_is_back_to_the_standard', { name: role.name }));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_reset_the_role'), describeError(caught)),
  });

  const differs = role.isSystem && (role.customized || (role.added?.length ?? 0) > 0 || (role.removed?.length ?? 0) > 0 || role.metadataDiffers);

  return (
    <ActionMenu label={tr('admin', 'actions_for_x', { name: role.name })}>
      <ActionMenuItem icon={<Pencil className="h-4 w-4" />} onSelect={() => onEdit(role)}>
        {role.isSystem ? tr('admin', 'edit_permissions') : tr('admin', 'edit')}
      </ActionMenuItem>
      <ActionMenuItem icon={<Copy className="h-4 w-4" />} onSelect={() => onClone(role)}>
        {tr('admin', 'duplicate')}
      </ActionMenuItem>
      {differs && (
        <ActionMenuItem icon={<RotateCcw className="h-4 w-4" />} onSelect={() => reset.mutate()}>
          {tr('admin', 'reset_to_standard')}
        </ActionMenuItem>
      )}
      {role.removable !== false && (!role.isSystem || role.removable) && (
        <>
          <ActionMenuSeparator />
          <ActionMenuItem
            icon={<Trash2 className="h-4 w-4" />}
            destructive
            onSelect={() => {
              if (role.userCount > 0) {
                toast.error(
                  tr('admin', 'that_role_is_still_in_use'),
                  tr('admin', 'x_staff_member_s_hold_it_move_them_to_another', { userCount: role.userCount }),
                );
                return;
              }
              remove.mutate();
            }}
          >
            {tr('admin', 'delete')}
          </ActionMenuItem>
        </>
      )}
    </ActionMenu>
  );
}

export function RolesTab({
  manageRoles,
  onEdit,
  onClone,
}: {
  manageRoles: boolean;
  onEdit: (role: Role) => void;
  onClone: (role: Role) => void;
}) {
  const toast = useToast();
  const queryClient = useQueryClient();
  const [open, setOpen] = useState<Record<string, boolean>>({ core: true });
  const groups = useQuery({ queryKey: ['role-groups'], queryFn: () => get<RoleGroup[]>('/org/roles/groups') });

  const addStandard = useMutation({
    mutationFn: (code: string) => post(`/org/roles/standard/${code}`),
    onSuccess: () => {
      toast.success(tr('admin', 'role_added'));
      void queryClient.invalidateQueries({ queryKey: ['roles'] });
      void queryClient.invalidateQueries({ queryKey: ['role-groups'] });
    },
    onError: (caught) => toast.error(tr('admin', 'could_not_add_the_role'), describeError(caught)),
  });

  if (groups.isLoading) return <p className="text-sm text-muted-foreground">{tr('admin', 'loading_roles')}</p>;
  if (groups.error) return <Alert variant="destructive">{describeError(groups.error)}</Alert>;

  return (
    <div className="space-y-2">
      {groups.data?.map((group) => {
        const expanded = open[group.module] ?? false;
        return (
          <div key={group.module} className="rounded-md border">
            <button
              type="button"
              className="flex w-full items-center gap-2 px-3 py-2 text-start focus-ring"
              onClick={() => setOpen({ ...open, [group.module]: !expanded })}
            >
              <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? '' : '-rotate-90'}`} />
              <span className="font-medium">{group.label}</span>
              <Badge variant="muted">{group.roles.length}</Badge>
              {group.state === 'read_only' && <Badge variant="warning">{tr('admin', 'archived')}</Badge>}
            </button>
            {expanded && (
              <div className="space-y-2 border-t p-3">
                {group.roles.map((role) => (
                  <div key={role.id} className="flex flex-wrap items-start justify-between gap-2 rounded border px-3 py-2">
                    <div className="min-w-0 flex-1">
                      <p className="font-medium">
                        {role.name}{' '}
                        <Badge variant={role.isSystem ? 'muted' : 'default'}>{role.isSystem ? tr('admin', 'standard') : tr('admin', 'custom')}</Badge>
                        {(role.added?.length ?? 0) > 0 && <Badge variant="secondary" className="ms-1">+{role.added?.length} {tr('admin', 'added')}</Badge>}
                        {(role.removed?.length ?? 0) > 0 && <Badge variant="warning" className="ms-1">{role.removed?.length} {tr('admin', 'removed_2')}</Badge>}
                        {role.metadataDiffers && <Badge variant="warning" className="ms-1">{tr('admin', 'differs_from_standard')}</Badge>}
                        {role.require2fa && <Badge variant="secondary" className="ms-1">{tr('admin', '2fa')}</Badge>}
                      </p>
                      <p className="text-xs text-muted-foreground">{role.description ?? '—'}</p>
                      <p className="text-xs text-muted-foreground">
                        {role.userCount} {tr('admin', 'staff_2')} {role.code === SUPER_ADMIN ? tr('admin', 'every_permission') : tr('admin', 'x_permissions', { length: role.permissions.length })}
                      </p>
                    </div>
                    {role.code === SUPER_ADMIN ? (
                      <span className="text-xs text-muted-foreground">{tr('admin', 'always_has_everything_your_plan_includes')}</span>
                    ) : manageRoles && group.state === 'live' ? (
                      <RoleActions role={role} onEdit={onEdit} onClone={onClone} />
                    ) : null}
                  </div>
                ))}
                {manageRoles && group.available.length > 0 && (
                  <div className="flex flex-wrap items-center gap-2 pt-1">
                    <span className="text-xs text-muted-foreground">{tr('admin', 'add_standard_role')}</span>
                    {group.available.map((template) => (
                      <Button
                        key={template.code}
                        size="sm"
                        variant="outline"
                        loading={addStandard.isPending && addStandard.variables === template.code}
                        onClick={() => addStandard.mutate(template.code)}
                        title={template.description}
                      >
                        <Plus className="h-3.5 w-3.5" /> {template.name}
                      </Button>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
